import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, type Vendor } from './api.js';
import { downloadResponsesWorkbook } from './exportResponses.js';
import { fileToLogoDataUrl } from './logoUpload.js';

/** Turns a company name into a URL-safe slug suggestion, matching the API's slug rules. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const statusLabels = { draft: 'Draft', published: 'Open', closed: 'Closed' } as const;
const anonymityLabels = {
  anonymous: 'Anonymous',
  opt_in: 'Employee chooses',
  identified: 'Identified',
} as const;

export function SurveyListPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: adminApi.vendors });
  const surveys = useQuery({ queryKey: ['surveys'], queryFn: () => adminApi.surveys() });

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [vendorError, setVendorError] = useState<string | null>(null);

  const createVendor = useMutation({
    mutationFn: () => adminApi.createVendor({ name: newName.trim(), slug: newSlug.trim() }),
    onSuccess: () => {
      setNewName('');
      setNewSlug('');
      setSlugEdited(false);
      setVendorError(null);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (error) =>
      setVendorError(error instanceof Error ? error.message : 'Could not add the company.'),
  });

  const removeVendor = useMutation({
    mutationFn: (vendorId: string) => adminApi.deleteVendor(vendorId),
    // A deleted vendor takes its surveys with it, so refresh both lists.
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (error) =>
      setVendorError(error instanceof Error ? error.message : 'Could not delete the company.'),
  });

  const deleteVendor = (vendor: Vendor) => {
    const surveyCount = surveys.data?.filter((survey) => survey.vendorId === vendor.id).length ?? 0;
    const consequence =
      surveyCount > 0
        ? ` This permanently deletes its ${surveyCount} survey${surveyCount === 1 ? '' : 's'} and ` +
          'every response collected, along with its employee list.'
        : ' Its employee list will also be removed.';
    if (window.confirm(`Delete “${vendor.name}”?${consequence} This cannot be undone.`)) {
      setVendorError(null);
      removeVendor.mutate(vendor.id);
    }
  };

  const canCreateVendor = newName.trim().length > 0 && /^[a-z0-9-]+$/.test(newSlug.trim());

  const act = useMutation({
    mutationFn: async ({ action, id }: { action: 'close' | 'clone' | 'remove'; id: string }) => {
      await adminApi[action](id);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const remove = (survey: { id: string; title: string; status: keyof typeof statusLabels }) => {
    if (survey.status !== 'draft') {
      const label = survey.title || 'Untitled';
      const confirmed = window.confirm(
        `Delete “${label}”? This survey is ${statusLabels[survey.status].toLowerCase()}, ` +
          'so any responses collected so far will be permanently deleted. This cannot be undone.',
      );
      if (!confirmed) return;
    }
    act.mutate({ action: 'remove', id: survey.id });
  };

  const download = async (surveyId: string) => {
    setDownloadError(null);
    setDownloadingId(surveyId);
    try {
      const data = await adminApi.responses(surveyId);
      await downloadResponsesWorkbook(data);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'Could not export responses.');
    } finally {
      setDownloadingId(null);
    }
  };

  const vendorName = (vendorId: string) =>
    vendors.data?.find((vendor) => vendor.id === vendorId)?.name ?? '—';
  const vendorSlug = (vendorId: string) =>
    vendors.data?.find((vendor) => vendor.id === vendorId)?.slug ?? '';

  const copyLink = async (survey: { id: string; vendorId: string; slug: string }) => {
    const url = `${window.location.origin}/${vendorSlug(survey.vendorId)}/${survey.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(survey.id);
      window.setTimeout(
        () => setCopiedId((current) => (current === survey.id ? null : current)),
        2000,
      );
    } catch {
      // Clipboard access can be blocked (e.g. an insecure context); fall back to a copyable prompt.
      window.prompt('Copy this survey link:', url);
    }
  };

  return (
    <>
      <div className="list-header">
        <h2>Surveys</h2>
        <Link className="button" to="/admin/surveys/new">
          New survey
        </Link>
      </div>

      {surveys.isPending && <p>Loading…</p>}
      {surveys.isError && <p role="alert">Could not load surveys: {surveys.error.message}</p>}
      {surveys.data?.length === 0 && <p>No surveys yet. Create one to get started.</p>}
      {downloadError && <p role="alert">{downloadError}</p>}

      {surveys.data && surveys.data.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Survey</th>
              <th>Company</th>
              <th>Status</th>
              <th>Anonymity</th>
              <th>Responses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {surveys.data.map((survey) => {
              const responses = survey.responseCount ?? 0;
              return (
                <tr key={survey.id}>
                  <td>
                    <Link to={`/admin/surveys/${survey.id}`}>{survey.title || 'Untitled'}</Link>
                    <br />
                    <code className="muted">
                      /{vendorSlug(survey.vendorId)}/{survey.slug}
                    </code>
                  </td>
                  <td>{vendorName(survey.vendorId)}</td>
                  <td>{statusLabels[survey.status]}</td>
                  <td>{anonymityLabels[survey.anonymityMode]}</td>
                  <td>{survey.status === 'draft' ? '—' : responses}</td>
                  <td className="actions">
                    <div>
                      {survey.status === 'published' && (
                        <button
                          type="button"
                          onClick={() => act.mutate({ action: 'close', id: survey.id })}
                        >
                          Close
                        </button>
                      )}
                      {survey.status === 'published' && (
                        <button
                          type="button"
                          onClick={() => copyLink(survey)}
                          title="Copy the shareable survey link"
                        >
                          {copiedId === survey.id ? 'Copied!' : 'Copy link'}
                        </button>
                      )}
                      {survey.status !== 'draft' && (
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/surveys/${survey.id}/data`)}
                          title="See who has and hasn't responded"
                        >
                          View data
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => download(survey.id)}
                        disabled={responses === 0 || downloadingId === survey.id}
                        title={
                          responses === 0
                            ? 'No responses to export yet'
                            : 'Download every response as an Excel spreadsheet'
                        }
                      >
                        {downloadingId === survey.id ? 'Preparing…' : 'Download responses'}
                      </button>
                      <button
                        type="button"
                        onClick={() => act.mutate({ action: 'clone', id: survey.id })}
                        title="Copy the questions into a new draft"
                      >
                        Clone
                      </button>
                      <button type="button" onClick={() => remove(survey)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h2>Companies</h2>

      <form
        className="add-company"
        onSubmit={(event) => {
          event.preventDefault();
          if (canCreateVendor) createVendor.mutate();
        }}
      >
        <label>
          Company name
          <input
            type="text"
            value={newName}
            placeholder="Acme Ltd"
            onChange={(event) => {
              setNewName(event.target.value);
              // Keep the slug in step with the name until the user edits it themselves.
              if (!slugEdited) setNewSlug(slugify(event.target.value));
            }}
          />
        </label>
        <label>
          URL slug
          <input
            type="text"
            value={newSlug}
            placeholder="acme-ltd"
            onChange={(event) => {
              setSlugEdited(true);
              setNewSlug(event.target.value);
            }}
          />
        </label>
        <button type="submit" disabled={!canCreateVendor || createVendor.isPending}>
          {createVendor.isPending ? 'Adding…' : 'Add company'}
        </button>
      </form>
      <p className="note">
        The slug is the company's part of its survey links: <code>/{newSlug || 'slug'}/…</code>
      </p>
      {vendorError && <p role="alert">{vendorError}</p>}

      {vendors.data?.length === 0 && <p>No companies yet.</p>}
      <ul className="company-list">
        {vendors.data?.map((vendor) => (
          <li key={vendor.id}>
            <VendorLogoManager vendor={vendor} />
            <div className="company-detail">
              <strong>{vendor.name}</strong>
              <code className="muted">/{vendor.slug}</code>
              <span>
                <Link to={`/admin/vendors/${vendor.id}/roster`}>manage employee list</Link>
              </span>
            </div>
            <button
              type="button"
              className="company-delete"
              onClick={() => deleteVendor(vendor)}
              disabled={removeVendor.isPending}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/** The logo thumbnail and its upload/remove controls for one company. */
function VendorLogoManager({ vendor }: { vendor: Vendor }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (logoUrl: string | null) => adminApi.updateVendorLogo(vendor.id, logoUrl),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendors'] }),
    onError: (mutationError) =>
      setError(mutationError instanceof Error ? mutationError.message : 'Could not save the logo.'),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const dataUrl = await fileToLogoDataUrl(file);
      save.mutate(dataUrl);
    } catch (conversionError) {
      setError(conversionError instanceof Error ? conversionError.message : 'Could not read image.');
    }
  };

  return (
    <div className="company-logo">
      <div className="company-logo-frame">
        {vendor.logoUrl ? (
          <img src={vendor.logoUrl} alt={`${vendor.name} logo`} />
        ) : (
          <span className="company-logo-empty">No logo</span>
        )}
      </div>
      <div className="company-logo-actions">
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          hidden
          onChange={(event) => {
            void onFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <button type="button" onClick={() => fileInput.current?.click()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : vendor.logoUrl ? 'Change logo' : 'Upload logo'}
        </button>
        {vendor.logoUrl && (
          <button type="button" onClick={() => save.mutate(null)} disabled={save.isPending}>
            Remove
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}
